import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Printer, RotateCcw, ShieldAlert } from 'lucide-react';
import { formatIqd, fromMillidinars, type PaymentMethod } from '@gc/shared';

import type { BoxBalanceSummaryDto, EmployeeBoxOverviewDto, EmployeeBoxRowDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { ListPager } from '@/features/customers/ListPager';
import { ApiError } from '@/lib/api';
import { BoxSplitFigures } from './BoxSplitFigures';
import { ResetBoxDialog } from './ResetBoxDialog';
import {
  fetchEmployeeBox,
  useBoxResets,
  useEmployeeBoxes,
  useMyBox,
  type BoxBalanceEntryAuditDto,
  type BoxBalanceSummaryAuditDto,
  type BoxResetAuditListResponse,
  type BoxResetAuditReceiptDto,
} from './useBoxBalance';

/**
 * Box balance — SPEC §12.1.
 *
 * The screen an employee reads before handing over. Everything about its layout
 * follows from one owner decision: the box counts account-balance payments as
 * well as cash, so the headline figure is intentionally larger than the notes in
 * the drawer. The split is therefore never collapsed, and the cash line is
 * repeated next to the reset button.
 */
export function BoxBalancePage() {
  const { t } = useTranslation();
  const { can, venue, employee } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const canSeeOthers = can('reports.view');
  // Resetting SOMEONE ELSE's box is the owner's alone (owner, 2026-09-23) —
  // not a permission key, so `can()` is the wrong question here. The server
  // refuses anyone else with `owner_only`; this only keeps the button away
  // from people it would refuse.
  const isOwner = employee?.isOwner === true;

  const { data: box, loading, error, reload } = useMyBox();
  const {
    data: overview,
    error: overviewError,
    reload: reloadOverview,
  } = useEmployeeBoxes(canSeeOthers);
  const [resetsPage, setResetsPage] = useState(1);
  const {
    data: resets,
    error: resetsError,
    reload: reloadResets,
  } = useBoxResets(canSeeOthers, resetsPage);

  const [resetting, setResetting] = useState(false);
  /** The other employee's box the owner is resetting, once it has loaded. */
  const [otherBox, setOtherBox] = useState<BoxBalanceSummaryDto | null>(null);
  const [openingOtherId, setOpeningOtherId] = useState<string | null>(null);
  const [otherError, setOtherError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<BoxResetAuditReceiptDto | null>(null);
  const [printedAt, setPrintedAt] = useState<Date>(() => new Date());

  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  function handleReset(receipt: BoxResetAuditReceiptDto): void {
    setResetting(false);
    setOtherBox(null);
    setConfirmation(receipt);
    void reload();
    void reloadOverview();
    // The new reset is the newest row: page one is where it lands. Already
    // there, the page does not change, so reload it explicitly.
    if (resetsPage === 1) void reloadResets();
    else setResetsPage(1);
  }

  /**
   * Open the reset dialog on another employee's box. The dialog needs the LIVE
   * split — it sends the figures it showed back as the stale-guard — so the
   * box is fetched fresh rather than lifted from the overview row.
   */
  async function openOtherReset(row: EmployeeBoxRowDto): Promise<void> {
    // The owner's own row is their own box: the ordinary dialog, own route.
    if (row.employeeId === employee?.id) {
      setResetting(true);
      return;
    }
    setOtherError(null);
    setOpeningOtherId(row.employeeId);
    try {
      setOtherBox(await fetchEmployeeBox(row.employeeId));
    } catch (caught) {
      setOtherError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : t('boxBalance.employees.openFailed'),
      );
    } finally {
      setOpeningOtherId(null);
    }
  }

  async function reloadOtherBox(employeeId: string): Promise<void> {
    try {
      setOtherBox(await fetchEmployeeBox(employeeId));
    } catch {
      // The dialog already shows the refusal that sent us here; a failed
      // re-read leaves the figures it has rather than blanking the form.
    }
  }

  function handlePrint(): void {
    // The timestamp on paper must be the moment the button was pressed, so it
    // is committed synchronously before the print dialog freezes the page.
    flushSync(() => setPrintedAt(new Date()));
    window.print();
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/*
       * Same print scoping as BillDetailsDrawer: hide by visibility so the
       * sheet, which lives deep in the tree, stays paintable. The sheet is
       * additionally display:none on screen — it exists only on paper.
       */}
      <style>{`
        .gc-box-print-sheet { display: none; }
        @media print {
          body * { visibility: hidden !important; }
          .gc-bill-print, .gc-bill-print * { visibility: visible !important; }
          .gc-bill-print {
            display: block !important;
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: none !important;
            border: 0 !important;
            box-shadow: none !important;
            background: #fff !important;
            color: #000 !important;
            padding: 0 !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      <header className="gc-no-print flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('boxBalance.title')}</h1>
        <div className="flex-1" />
        {box ? (
          <>
            {/* Cash in hand is what the cashier answers for at handover —
                it reads at display size, not as a caption. */}
            <span className="text-sm text-content-secondary">
              {t('boxBalance.cashInHand')}:{' '}
              <span className="tabular text-lg font-bold text-primary [text-shadow:var(--glow-primary)]">
                {money(box.split.cashMd)}
              </span>
            </span>
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('boxBalance.print.button')}
            </button>
            <button
              type="button"
              onClick={() => setResetting(true)}
              disabled={box.split.entryCount === 0}
              title={box.split.entryCount === 0 ? t('boxBalance.resetDialog.empty') : undefined}
              className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              {t('boxBalance.reset')}
            </button>
          </>
        ) : null}
      </header>

      {box ? (
        <BoxPrintSheet
          box={box}
          venueName={venue?.name ?? ''}
          currencySymbol={currencySymbol}
          printedAt={printedAt}
        />
      ) : null}

      <div className="min-h-0 flex-1 space-y-6 px-4 pb-6">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !box ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : box ? (
          <>
            <section>
              <div className="mb-2 flex items-baseline gap-3">
                <h2 className="text-base font-semibold text-content">{t('boxBalance.myBox')}</h2>
                <p className="text-xs text-content-muted">{t('boxBalance.subtitle')}</p>
              </div>

              <BoxSplitFigures split={box.split} currencySymbol={currencySymbol} />

              {/* The denormalised running total and the entries behind it are
                  written in one transaction, so a mismatch is a bug. Saying so
                  beats letting a handover be signed against a wrong figure. */}
              {!box.isReconciled ? (
                <p
                  role="alert"
                  className="mt-3 flex items-start gap-2 rounded-card border border-danger/40 p-3 text-sm text-danger"
                >
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    {t('boxBalance.mismatch', {
                      stored: money(box.boxBalanceMd),
                      computed: money(box.split.totalMd),
                    })}
                  </span>
                </p>
              ) : null}

              <p className="mt-3 text-xs text-content-muted">
                {t('boxBalance.lastReset')}: {lastResetLine(box, t)}
                {' · '}
                {t('boxBalance.entryCount')}: {box.split.entryCount}
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-base font-semibold text-content">
                {t('boxBalance.entries.title')}
              </h2>
              {box.entries.length === 0 ? (
                <p className="text-sm text-content-muted">{t('boxBalance.noEntries')}</p>
              ) : (
                <EntryTable entries={box.entries} currencySymbol={currencySymbol} />
              )}
            </section>

            {canSeeOthers ? (
              <EmployeeBoxesSection
                overview={overview}
                error={overviewError}
                currencySymbol={currencySymbol}
                onReset={isOwner ? (row) => void openOtherReset(row) : undefined}
                openingId={openingOtherId}
                actionError={otherError}
              />
            ) : null}

            {canSeeOthers ? (
              <ResetHistorySection
                resets={resets}
                error={resetsError}
                currencySymbol={currencySymbol}
                onPage={setResetsPage}
              />
            ) : null}
          </>
        ) : null}
      </div>

      {resetting && box ? (
        <ResetBoxDialog
          box={box}
          currencySymbol={currencySymbol}
          onClose={() => setResetting(false)}
          onReset={handleReset}
          // A refused reset means the figures on screen are stale; re-fetching
          // here is what lets the open dialog re-render the NEW split for a
          // recount instead of showing the numbers the server just rejected.
          onBoxChanged={() => void reload()}
        />
      ) : null}

      {otherBox ? (
        <ResetBoxDialog
          box={otherBox}
          currencySymbol={currencySymbol}
          forOther
          onClose={() => setOtherBox(null)}
          onReset={handleReset}
          onBoxChanged={() => void reloadOtherBox(otherBox.employeeId)}
        />
      ) : null}

      {confirmation ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-card border border-subtle bg-ground p-5 shadow-2xl"
          >
            <h2 className="text-lg font-semibold text-content">
              {confirmation.reset.employeeId === employee?.id
                ? t('boxBalance.resetDialog.done', { total: money(confirmation.reset.totalMd) })
                : t('boxBalance.resetDialog.doneOther', {
                    total: money(confirmation.reset.totalMd),
                    name: confirmation.reset.employeeName,
                  })}
            </h2>
            {/* Repeated after the fact as well: this is the figure that will be
                reconciled against the drawer tomorrow morning. */}
            <dl className="tabular mt-3 rounded-card bg-raised p-3 text-sm">
              <Row label={t('boxBalance.cash')} value={money(confirmation.reset.cashMd)} />
              <Row
                label={t('boxBalance.accountBalance')}
                value={money(confirmation.reset.accountBalanceMd)}
              />
              {/* FIB and the rest, swept with the box but never in the drawer.
                  Absent when there was none, as the box screen shows it. */}
              {isNonZero(confirmation.reset.electronicMd) ? (
                <Row
                  label={t('boxBalance.electronic')}
                  value={money(confirmation.reset.electronicMd)}
                />
              ) : null}
              {/* Only when a count was made — an uncounted reset must not
                  render as "counted 0". */}
              {confirmation.reset.countedCashMd != null ? (
                <Row
                  label={t('boxBalance.history.counted')}
                  value={money(confirmation.reset.countedCashMd)}
                />
              ) : null}
              {confirmation.reset.varianceMd != null ? (
                <Row
                  label={t('boxBalance.history.variance')}
                  value={signedMoney(confirmation.reset.varianceMd, money)}
                />
              ) : null}
              <Row
                label={t('boxBalance.employees.mainAccount')}
                value={money(confirmation.mainAccountBalanceMd)}
              />
              {confirmation.reset.resetBy ? (
                <Row
                  label={t('boxBalance.history.resetBy')}
                  value={confirmation.reset.resetBy.name}
                />
              ) : null}
            </dl>
            <button
              type="button"
              onClick={() => setConfirmation(null)}
              className="mt-4 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Whether an electronic figure is worth a line or a column (2026-09-24). A
 * venue that takes no electronic payments keeps the screen it had; `undefined`
 * is a server from before the component existed.
 */
function isNonZero(md: string | undefined): md is string {
  return md !== undefined && md !== '0';
}

/**
 * A variance is a signed judgement, not an amount of money changing hands, so
 * the positive direction gets an explicit "+" — "2,000" and "+2,000" read very
 * differently in an audit column.
 */
function signedMoney(md: string, money: (value: string) => string): string {
  return BigInt(md) > 0n ? `+${money(md)}` : money(md);
}

/**
 * The paper snapshot behind "Print box balance" (wave 3): the current split,
 * the entry count, who the box belongs to and when the sheet was printed.
 * Screen-hidden (`.gc-box-print-sheet`), print-visible (`.gc-bill-print`) —
 * the BillDetailsDrawer scoping. Exported so the content rules — the split is
 * never collapsed, and the sheet says it is a snapshot, not a reset — are
 * pinned by tests.
 */
export function BoxPrintSheet({
  box,
  venueName,
  currencySymbol,
  printedAt,
}: {
  box: BoxBalanceSummaryAuditDto;
  venueName: string;
  currencySymbol: string;
  printedAt: Date;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  return (
    <div className="gc-bill-print gc-box-print-sheet p-4">
      <h2 className="text-lg font-semibold">{t('boxBalance.print.title')}</h2>
      {venueName ? <p className="text-sm">{venueName}</p> : null}

      <dl className="tabular mt-3 text-sm">
        <PrintRow label={t('boxBalance.print.employee')} value={box.employeeName} />
        <PrintRow label={t('boxBalance.print.printedAt')} value={printedAt.toLocaleString()} />
        <PrintRow label={t('boxBalance.total')} value={money(box.split.totalMd)} />
        <PrintRow label={t('boxBalance.cash')} value={money(box.split.cashMd)} />
        <PrintRow
          label={t('boxBalance.accountBalance')}
          value={money(box.split.accountBalanceMd)}
        />
        {isNonZero(box.split.electronicMd) ? (
          <PrintRow label={t('boxBalance.electronic')} value={money(box.split.electronicMd)} />
        ) : null}
        <PrintRow label={t('boxBalance.entryCount')} value={String(box.split.entryCount)} />
        <PrintRow label={t('boxBalance.lastReset')} value={lastResetLine(box, t)} />
      </dl>

      <p className="mt-3 text-xs">{t('boxBalance.print.note')}</p>
    </div>
  );
}

/**
 * "23/09/2026, 20:14 — by Rawa". Since the owner may reset anyone's box
 * (2026-09-23), an employee reading their own screen needs to know it was not
 * them; an unknown performer is left unsaid rather than guessed.
 */
export function lastResetLine(
  box: Pick<BoxBalanceSummaryAuditDto, 'lastResetAt' | 'lastResetByName'>,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!box.lastResetAt) return t('boxBalance.never');
  const when = new Date(box.lastResetAt).toLocaleString();
  return box.lastResetByName
    ? `${when} — ${t('boxBalance.lastResetBy', { name: box.lastResetByName })}`
    : when;
}

function PrintRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-6 border-b border-black/20 py-1">
      <dt>{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/**
 * Whether an overview row is the Portal's box: customers spending their
 * balance on the PCs, never notes. It is history; a reset would sweep money
 * that never existed as cash into the safe (11.2M, observed 2026-10-12).
 *
 * Read from the flag the API sets from the actor's username, never from the
 * display name: the name is editable data, so renaming the Portal actor would
 * bring Reset back on its row, and a real employee called "Portal" would lose
 * it.
 */
export function isPortalBoxRow(row: Pick<EmployeeBoxRowDto, 'isSelfService'>): boolean {
  return row.isSelfService;
}

/**
 * Every open box, for the owner (§12.1). Exported so the error-before-empty
 * rule is pinned by tests without fetching: a failed load renders the error IN
 * PLACE of the table — a section that silently vanishes on a 500/403 reads as
 * "no open boxes", which is a claim about money nobody verified.
 *
 * The Portal's row has figures and no Reset button (2026-10-12): the server
 * refuses that reset for good, so the cell says why instead.
 */
export function EmployeeBoxesSection({
  overview,
  error,
  currencySymbol,
  onReset,
  openingId = null,
  actionError = null,
}: {
  overview: EmployeeBoxOverviewDto | null;
  error: string | null;
  currencySymbol: string;
  /**
   * Present for the OWNER only (owner, 2026-09-23): a Reset button per box.
   * Absent, the table is read-only — the button is not rendered disabled
   * for everyone else, because a control you may never use is just noise.
   */
  onReset?: (row: EmployeeBoxRowDto) => void;
  /** The box whose live figures are being fetched for the dialog. */
  openingId?: string | null;
  /** Why the last box could not be opened for a reset. */
  actionError?: string | null;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  // Error before empty/loading: an error with stale data behind it still
  // reports the error — the stale table must not pass for current fact.
  if (error) {
    return (
      <section>
        <h2 className="mb-2 text-base font-semibold text-content">
          {t('boxBalance.employees.title')}
        </h2>
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      </section>
    );
  }
  if (!overview) return null;

  // Its own column only when some box holds electronic money (2026-09-24).
  const showElectronic =
    isNonZero(overview.summary.electronicMd) ||
    overview.rows.some((row) => isNonZero(row.split.electronicMd));

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <h2 className="text-base font-semibold text-content">{t('boxBalance.employees.title')}</h2>
        <span className="tabular text-xs text-content-secondary">
          {t('boxBalance.employees.mainAccount')}: {money(overview.mainAccountBalanceMd)}
        </span>
      </div>

      {actionError ? (
        <p role="alert" className="mb-2 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full text-sm">
          <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
            <tr>
              <Th>{t('boxBalance.employees.employee')}</Th>
              <Th align="end">{t('boxBalance.total')}</Th>
              <Th align="end">{t('boxBalance.cash')}</Th>
              <Th align="end">{t('boxBalance.accountBalance')}</Th>
              {showElectronic ? <Th align="end">{t('boxBalance.electronic')}</Th> : null}
              <Th align="end" className="max-md:hidden">
                {t('boxBalance.entryCount')}
              </Th>
              <Th>{t('boxBalance.lastReset')}</Th>
              {onReset ? (
                <Th>
                  <span className="sr-only">{t('boxBalance.reset')}</span>
                </Th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {overview.rows.map((row) => (
              <tr key={row.employeeId} className="border-t border-subtle">
                <Td>{row.employeeName}</Td>
                <Td align="end" strong>
                  {money(row.split.totalMd)}
                </Td>
                <Td align="end">{money(row.split.cashMd)}</Td>
                <Td align="end">{money(row.split.accountBalanceMd)}</Td>
                {showElectronic ? (
                  <Td align="end">{money(row.split.electronicMd ?? '0')}</Td>
                ) : null}
                <Td align="end" className="max-md:hidden">
                  {row.split.entryCount}
                </Td>
                <Td>
                  {row.lastResetAt
                    ? new Date(row.lastResetAt).toLocaleString()
                    : t('boxBalance.never')}
                </Td>
                {onReset && isPortalBoxRow(row) ? (
                  <Td>
                    <span
                      data-portal-box
                      title={t('boxBalance.resetDialog.portalNotResettable')}
                      className="whitespace-nowrap text-xs text-content-muted"
                    >
                      {t('boxBalance.employees.portalHistory')}
                    </span>
                  </Td>
                ) : onReset ? (
                  <Td>
                    <button
                      type="button"
                      data-reset-box={row.employeeId}
                      onClick={() => onReset(row)}
                      // Nothing to sweep is refused by the server as
                      // `box_already_empty`; say so before the click instead.
                      disabled={row.split.entryCount === 0 || openingId !== null}
                      title={t('boxBalance.employees.resetOneTitle', { name: row.employeeName })}
                      aria-label={t('boxBalance.employees.resetOneTitle', {
                        name: row.employeeName,
                      })}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                      {openingId === row.employeeId
                        ? t('common.loading')
                        : t('boxBalance.employees.resetOne')}
                    </button>
                  </Td>
                ) : null}
              </tr>
            ))}
            <tr className="border-t border-strong bg-raised font-semibold">
              <Td>{t('boxBalance.employees.allBoxes')}</Td>
              <Td align="end">{money(overview.summary.totalMd)}</Td>
              <Td align="end">{money(overview.summary.cashMd)}</Td>
              <Td align="end">{money(overview.summary.accountBalanceMd)}</Td>
              {showElectronic ? (
                <Td align="end">{money(overview.summary.electronicMd ?? '0')}</Td>
              ) : null}
              <Td align="end" className="max-md:hidden">
                {overview.summary.entryCount}
              </Td>
              <Td />
              {onReset ? <Td /> : null}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The reset audit log (§12.1), with the counted-cash columns of WAVE2-DESIGN
 * §4. Exported so the rendering rules that matter here — null renders as "not
 * counted" and never as zero, the variance footer splits counted from
 * uncounted resets, and a failed load renders its error in place of the table
 * (error-before-empty) — can be pinned by tests without fetching.
 */
export function ResetHistorySection({
  resets,
  error = null,
  currencySymbol,
  onPage,
}: {
  resets: BoxResetAuditListResponse | null;
  error?: string | null;
  currencySymbol: string;
  /** Turns the page; the history is paged, its footer totals every reset. */
  onPage?: (page: number) => void;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  if (error) {
    return (
      <section>
        <h2 className="mb-2 text-base font-semibold text-content">
          {t('boxBalance.history.title')}
        </h2>
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      </section>
    );
  }
  if (!resets) return null;

  // `BoxReset.electronicMd` — a column only once some reset swept any.
  const showElectronic =
    isNonZero(resets.summary.electronicMd) ||
    resets.rows.some((row) => isNonZero(row.electronicMd));

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <h2 className="text-base font-semibold text-content">{t('boxBalance.history.title')}</h2>
        <p className="text-xs text-content-muted">{t('boxBalance.history.auditNote')}</p>
      </div>

      {resets.rows.length === 0 ? (
        <p className="text-sm text-content-muted">{t('boxBalance.history.empty')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-card border border-subtle">
            <table className="w-full text-sm">
              <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
                <tr>
                  <Th>{t('boxBalance.history.when')}</Th>
                  <Th>{t('boxBalance.history.employee')}</Th>
                  <Th>{t('boxBalance.history.resetBy')}</Th>
                  <Th align="end">{t('boxBalance.history.moved')}</Th>
                  <Th align="end">{t('boxBalance.cash')}</Th>
                  <Th align="end">{t('boxBalance.history.counted')}</Th>
                  <Th align="end">{t('boxBalance.history.variance')}</Th>
                  <Th align="end">{t('boxBalance.accountBalance')}</Th>
                  {showElectronic ? <Th align="end">{t('boxBalance.electronic')}</Th> : null}
                  <Th align="end" className="max-md:hidden">
                    {t('boxBalance.entryCount')}
                  </Th>
                  <Th>{t('boxBalance.history.note')}</Th>
                </tr>
              </thead>
              <tbody>
                {resets.rows.map((row) => (
                  <tr key={row.id} className="border-t border-subtle">
                    <Td>{new Date(row.resetAt).toLocaleString()}</Td>
                    <Td>{row.employeeName}</Td>
                    {/* Who pressed Reset (owner, 2026-09-23). A dash on resets
                        recorded before the row kept it: unknown, and not
                        guessed as the box's own employee. */}
                    <Td>
                      <span data-reset-by={row.resetBy?.id ?? ''}>{row.resetBy?.name ?? '—'}</span>
                    </Td>
                    <Td align="end" strong>
                      {money(row.totalMd)}
                    </Td>
                    <Td align="end">{money(row.cashMd)}</Td>
                    {/* Null is "not counted", NEVER rendered as zero: a zero
                        here would claim a count that was never made. */}
                    <Td align="end">
                      {row.countedCashMd == null
                        ? t('boxBalance.history.uncounted')
                        : money(row.countedCashMd)}
                    </Td>
                    <Td align="end">
                      {row.varianceMd == null ? '—' : signedMoney(row.varianceMd, money)}
                    </Td>
                    <Td align="end">{money(row.accountBalanceMd)}</Td>
                    {showElectronic ? <Td align="end">{money(row.electronicMd ?? '0')}</Td> : null}
                    <Td align="end">{row.entryCount}</Td>
                    <Td>{row.note ?? '—'}</Td>
                  </tr>
                ))}
                <tr className="border-t border-strong bg-raised font-semibold">
                  <Td />
                  <Td>{t('common.total')}</Td>
                  <Td />
                  <Td align="end">{money(resets.summary.totalMd)}</Td>
                  <Td align="end">{money(resets.summary.cashMd)}</Td>
                  <Td />
                  <Td align="end">{signedMoney(resets.varianceTotalMd, money)}</Td>
                  <Td align="end">{money(resets.summary.accountBalanceMd)}</Td>
                  {showElectronic ? (
                    <Td align="end">{money(resets.summary.electronicMd ?? '0')}</Td>
                  ) : null}
                  <Td align="end">{resets.summary.entryCount}</Td>
                  <Td />
                </tr>
              </tbody>
            </table>
          </div>

          {onPage ? (
            <ListPager
              page={resets.page}
              hasMore={resets.hasMore}
              onPage={onPage}
              previousLabel={t('boxBalance.history.previousPage')}
              nextLabel={t('boxBalance.history.nextPage')}
            />
          ) : null}

          {/* The counts are the context that makes the total honest: variance
              0 across 0 counted resets proves nothing at all. */}
          <p className="mt-2 text-xs text-content-muted">
            {t('boxBalance.history.varianceFooter', {
              total: signedMoney(resets.varianceTotalMd, money),
              countedCount: resets.countedResets,
              uncountedCount: resets.uncountedResets,
            })}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * The label for how an entry was paid. A full record rather than "cash, else
 * account balance": that else labelled an electronic payment (2026-09-24) as
 * balance money, and a new method must fail to compile here instead.
 */
const METHOD_LABEL_KEY: Record<PaymentMethod, string> = {
  CASH: 'payments.cash',
  ACCOUNT_BALANCE: 'payments.accountBalance',
  ELECTRONIC: 'payments.electronic',
};

/** Exported so the method column, provider included, is pinned by tests. */
export function EntryTable({
  entries,
  currencySymbol,
}: {
  entries: BoxBalanceEntryAuditDto[];
  currencySymbol: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-card border border-subtle">
      <table className="w-full text-sm">
        <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
          <tr>
            <Th>{t('boxBalance.entries.when')}</Th>
            <Th>{t('boxBalance.entries.invoice')}</Th>
            <Th>{t('boxBalance.entries.party')}</Th>
            <Th>{t('boxBalance.entries.source')}</Th>
            <Th>{t('boxBalance.entries.method')}</Th>
            <Th align="end">{t('boxBalance.entries.amount')}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-t border-subtle">
              <Td>{new Date(entry.createdAt).toLocaleString()}</Td>
              <Td>{entry.invoiceNumber ?? '—'}</Td>
              <Td>{entry.partyName ?? '—'}</Td>
              <Td>{entry.billKind ? t(`boxBalance.kind.${entry.billKind}`) : '—'}</Td>
              <Td>
                <span
                  className={entry.method === 'CASH' ? 'text-content-secondary' : 'text-ending'}
                >
                  {t(METHOD_LABEL_KEY[entry.method])}
                  {/* "— FIB": what the cashier checks in the provider's app. */}
                  {entry.electronicProviderLabel ? ` — ${entry.electronicProviderLabel}` : ''}
                </span>
              </Td>
              <Td align="end" strong>
                {formatIqd(fromMillidinars(entry.amountMd), { currencySymbol })}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align,
  className = '',
}: {
  children?: React.ReactNode;
  align?: 'end';
  className?: string;
}) {
  return (
    <th
      className={`px-3 py-2 font-medium max-md:px-2 ${align === 'end' ? 'text-end' : 'text-start'} ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  strong,
  className = '',
}: {
  children?: React.ReactNode;
  align?: 'end';
  strong?: boolean;
  className?: string;
}) {
  return (
    <td
      className={[
        'px-3 py-2 max-md:px-2',
        className,
        align === 'end' ? 'tabular text-end' : 'text-start',
        strong ? 'font-medium text-content' : 'text-content-secondary',
      ].join(' ')}
    >
      {children}
    </td>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-0.5">
      <dt className="text-content-secondary">{label}</dt>
      <dd className="text-content">{value}</dd>
    </div>
  );
}
